/**
 * The assistant button in the Bitrix24 iframe's header (#765, spec §8.4):
 * the record on screen, or — with none — the whole base once it is released.
 * The same choice as the app header's button; only the styling is the
 * iframe's. Renders nothing where the session has no assistant
 * (`features.assistant` off → no drawer context) or nothing to ask about —
 * the provider projects no record a subject's access may not ask about
 * (#1677), and the whole base refused to it is no topic either. A turn in
 * flight is always something to return to (R6): the button then reopens the
 * running conversation, which `openAssistant` keeps while it is busy.
 *
 * A plan below the assistant's floor (the session's verdict
 * `tier_not_eligible`, #1897) is the exception: the button stays, with a lock,
 * and opens the shared tier screen — the iframe has no payment, so it says
 * where the account owner changes the plan. Only for a subject who would have
 * something to ask once the plan allows it: the provider's own rule for
 * `available` (the record, or the whole base), asked of the subject directly
 * because the provider is not mounted while the assistant is off. A grant
 * holder who may ask about nothing (#1677) gets no upsell for a button it could
 * never use. A switched-off assistant (`feature_disabled`) and a session that
 * carries no verdict stay as above: no button.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles } from 'lucide-react';
import { ASSISTANT_FLAG_KEY } from '@aica/core';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { useScreenSubject } from '@/contexts/ScreenSubjectContext';
import { useOrgTopic } from '@/hooks/assistant/useOrgTopic';
import { canAskAboutRecord } from '@/lib/route-permissions';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@/components/ui/modal';
import { TierLockedScreen } from '@/components/ui/TierLockedScreen';

const BUTTON_CLASS =
  'ml-auto flex items-center gap-2 rounded-button px-3 py-2 text-sm font-medium text-ai transition-colors hover:bg-bg-elevated hover:text-ai-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function EmbeddedAssistantButton() {
  const { t } = useTranslation('assistant');
  const drawer = useAssistantDrawerSafe();
  const orgTopic = useOrgTopic();
  const subject = useScreenSubject();
  const { gate } = useFeatureFlagsSafe();
  const [lockedOpen, setLockedOpen] = useState(false);

  const locked = gate(ASSISTANT_FLAG_KEY) === 'tier_not_eligible';
  const topic =
    drawer?.screenTopic ?? orgTopic ?? (drawer?.busy ? drawer.topic : null) ?? null;

  const openAssistant = () => {
    if (drawer && topic) drawer.openAssistant(topic);
  };

  if (locked ? !(canAskAboutRecord(subject) || orgTopic !== null) : !drawer || !topic) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={locked ? () => setLockedOpen(true) : openAssistant}
        className={BUTTON_CLASS}
        data-testid="embedded-assistant-button"
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        {t('openButton')}
        {locked && (
          <Lock
            className="h-3 w-3 text-fg-muted"
            aria-hidden="true"
            data-testid="embedded-assistant-lock"
          />
        )}
      </button>
      {locked && (
        <Modal open={lockedOpen} onOpenChange={setLockedOpen}>
          <ModalContent size="md" aria-describedby={undefined}>
            <ModalHeader>
              <ModalTitle className="sr-only">{t('openButton')}</ModalTitle>
            </ModalHeader>
            {/* A short Bitrix24 iframe is lower than the screen with its
                preview: the body scrolls while the dialog and its close
                button stay in the viewport (the WhatsNewDialog pattern). */}
            <div className="max-h-[60vh] overflow-y-auto">
              <TierLockedScreen flag={ASSISTANT_FLAG_KEY} compact />
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
